import { FileAddOutlined } from '@ant-design/icons'
import { useRateManagement } from '@api/useFreight/useFreight'
import type { PointToPoint } from '@api/useFreight/types'
import { GraviButton, Horizontal, Texto, useLocalStorage } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { DatePicker, Form, InputNumber, message, Popover, Switch, Tooltip } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useState } from 'react'
import type { RateFormValues } from './BaseScheduleRateButton'

export interface PointToPointScheduleButtonProps {
  pointToPoint: PointToPoint
  onSuccess?: () => void
  setPointToPoint: (p: PointToPoint) => void
}

export function PointToPointScheduleButton({
  pointToPoint,
  onSuccess,
  setPointToPoint,
}: PointToPointScheduleButtonProps) {
  const queryClient = useQueryClient()
  const [form] = Form.useForm<RateFormValues>()
  const [popoverVisible, setPopoverVisible] = useState(false)
  const { value: showDistance, setValue: setShowDistance } = useLocalStorage('point-to-point-show-distance', false)
  const { value: showEffectiveTo, setValue: setShowEffectiveTo } = useLocalStorage(
    'point-to-point-show-effective-to',
    false
  )

  const { useCreatePointToPointRate } = useRateManagement()
  const createRateMutation = useCreatePointToPointRate()

  const handleScheduleRate = async (values: RateFormValues) => {
    try {
      const effectiveDate = values.effective_date.toISOString()
      const effectiveToDate = showEffectiveTo && values.effective_to ? values.effective_to.toISOString() : null

      const updatedPointToPoint = await createRateMutation.mutateAsync({
        point_to_point_id: pointToPoint._id || pointToPoint.id,
        effective_date: effectiveDate,
        effective_to_date: effectiveToDate,
        rate: values.rate,
        distance: showDistance && values.distance ? Number(values.distance) : undefined,
      })

      // Update the drawer state with the new data
      setPointToPoint(updatedPointToPoint)

      message.success('Rate scheduled successfully')
      setPopoverVisible(false)

      if (onSuccess) {
        await queryClient.invalidateQueries({
          queryKey: ['pointToPointRates'],
        })
        onSuccess()
      }
    } catch (error) {
      console.error('Failed to schedule rate:', error)
      message.error('Failed to schedule rate')
    }
  }

  // Get the latest rate to pre-fill the schedule form
  const getLatestRate = (): PointToPoint['rates'][0] | null => {
    if (!pointToPoint.rates || pointToPoint.rates.length === 0) return null

    // Sort rates by effective_from date in descending order
    const sortedRates = [...pointToPoint.rates].sort((a, b) => {
      return new Date(b.effective_from).getTime() - new Date(a.effective_from).getTime()
    })

    return sortedRates[0]
  }

  const renderPopoverContent = () => {
    const latestRate = getLatestRate()
    const defaultScheduleDate = latestRate ? dayjs(latestRate.effective_from) : dayjs()

    return (
      <Form
        form={form}
        layout='vertical'
        style={{ width: 200 }}
        onFinish={handleScheduleRate}
        initialValues={{
          effective_date: defaultScheduleDate,
          rate: latestRate?.rate || 0,
          distance: latestRate?.distance || 0,
        }}
      >
        <Form.Item
          name='rate'
          label='Rate'
          rules={[
            { required: true, message: 'Please enter rate' },
            {
              type: 'number',
              min: 0.0001,
              message: 'Rate must be greater than 0',
            },
          ]}
        >
          <InputNumber prefix='$' precision={5} min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item label='Distance' style={{ marginBottom: 16 }}>
          <Horizontal justifyContent='space-between' verticalCenter gap={8} style={{ marginBottom: 8 }}>
            <Switch
              checked={showDistance}
              onChange={(checked) => {
                setShowDistance(checked)
                if (!checked) {
                  form.setFieldValue('distance', null)
                }
              }}
            />
          </Horizontal>
          {showDistance ? (
            <Form.Item name='distance' noStyle>
              <InputNumber addonAfter='miles' min={0} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          ) : (
            <Texto appearance='hint'> </Texto>
          )}
        </Form.Item>

        <Form.Item
          name='effective_date'
          label='Effective From'
          rules={[{ required: true, message: 'Please select effective date' }]}
        >
          <DatePicker format='MM/DD/YYYY' style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item>
          <Horizontal verticalCenter gap={8}>
            <Switch checked={showEffectiveTo} onChange={setShowEffectiveTo} />
            <Texto>Set end date</Texto>
          </Horizontal>
        </Form.Item>

        {showEffectiveTo && (
          <Form.Item
            name='effective_to'
            label='Effective To'
            rules={[
              {
                required: showEffectiveTo,
                message: 'Please select end date',
              },
              {
                validator: (_, value) => {
                  if (showEffectiveTo && value) {
                    const startDate = form.getFieldValue('effective_date')
                    if (startDate && value.isBefore(startDate)) {
                      return Promise.reject('End date must be after start date')
                    }
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <DatePicker format='MM/DD/YYYY' style={{ width: '100%' }} />
          </Form.Item>
        )}

        <Horizontal justifyContent='flex-end' gap={8}>
          <GraviButton appearance='outlined' onClick={() => setPopoverVisible(false)}>
            Cancel
          </GraviButton>
          <GraviButton appearance='solid' theme2 htmlType='submit' loading={createRateMutation.isPending}>
            Schedule
          </GraviButton>
        </Horizontal>
      </Form>
    )
  }

  return (
    <Tooltip title='Schedule New Rate'>
      <Popover
        content={renderPopoverContent()}
        title='Schedule New Rate'
        trigger='click'
        placement='topRight'
        open={popoverVisible}
        onOpenChange={(visible) => {
          if (visible) {
            const latestRate = getLatestRate()
            const defaultScheduleDate = latestRate ? dayjs(latestRate.effective_from) : dayjs()

            const defaultEffectiveToDate = showEffectiveTo
              ? latestRate?.effective_to
                ? dayjs(latestRate.effective_to)
                : dayjs().add(30, 'days')
              : null

            setShowDistance(!!latestRate?.distance)

            form.setFieldsValue({
              effective_date: defaultScheduleDate,
              effective_to: defaultEffectiveToDate || undefined,
              rate: latestRate?.rate || 0,
              distance: latestRate?.distance || undefined,
            })
          }
          setPopoverVisible(visible)
        }}
      >
        <GraviButton
          theme2
          loading={createRateMutation.isPending}
          icon={<FileAddOutlined />}
          buttonText='Schedule New Rate'
          onClick={() => setPopoverVisible(true)}
        />
      </Popover>
    </Tooltip>
  )
}
